import { BetActionErrorResponseDataDto } from 'api/common.generated';
import cn from 'classnames';
import BetslipCouponCard from 'features/coupon/component/BetslipCouponCard';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BetCalculator from 'shared/components/calculators/BetCalculator';
import { BONUS_BALANCE } from 'shared/constants/common';
import { BetslipEvents, setDataLayer } from 'shared/constants/datalayer';
import { FINANCIAL_THRESHOLDS } from 'shared/constants/financial-thresholds';
import { BETSType, CouponState, PlaceBet, State } from 'shared/interface/index';
import * as actionTypes from 'shared/store/action-types';
import { createAction } from 'shared/util/utility';
import { getAnalyticalData } from '../../../shared/util/analytical';
import { usePromoConditionsStateTypes } from '../../PromoConditions';
import { AkoBonusData } from '../interface';
import AkoBonusInfoToast from './AkoBonusInfoToast';
import ErrorCodes from './errorCode';

interface AKOBetProps {
	allBets: PlaceBet[];
	removeBet: (id: string) => void;
	betStatics: any;
	onChangeStackValue: (value: number) => void;
	handelSelectedBet: (value: boolean, id: string) => void;
	handleOddsChangeOption: (value: string) => void;
	onClickPlaceBet: () => void;
	stackValue: number;
	changeNavigationView: () => void;
	errorCode: number[];
	loading: boolean;
	freeBets: any[];
	selectedFreeBet: string;
	onCloseError: (error: number, indexOfErrorDetail: number) => void;
	betLoading: boolean;
	betType: string;
	approvedSlip: boolean;
	betActionErrors: BetActionErrorResponseDataDto[];
	warnings: string[];
	completedEvents: string[];
	selectedBalanceType?: string;
	handleIsRestoringBets: (isRestoring: boolean) => void;
	isCal: boolean;
	handleIsCal: (value: boolean) => void;
	setStakeMethod: (stake: string) => void;
	bets: BETSType;
	promoConditionsState: usePromoConditionsStateTypes;
}

const AKOBet = forwardRef((props: AKOBetProps, ref) => {
	const { onChangeStackValue, allBets, isCal, handleIsCal, setStakeMethod } = props;
	const dispatch = useDispatch();

	const temp = props.allBets.length - props.allBets.filter((item) => !item.selectedBet).length;

	const couponContentRef = useRef<HTMLHeadingElement>(null);
	const couponContentWrapperRef = useRef<HTMLHeadingElement>(null);

	const coupon = useSelector<State, CouponState>((state) => state.coupon);
	const { enabledBets, disabledBets } = useSelector((state: State) => state.coupon.analytics);

	const [couponContentHeight, setCouponContentHeight] = useState<number | string>('100%');

	const onCancelCal = useCallback(() => {
		handleIsCal(false);
	}, [handleIsCal]);

	const onConfirmCal = useCallback(
		(value: number) => {
			handleIsCal(false);
			onChangeStackValue(value);
		},
		[handleIsCal, onChangeStackValue],
	);

	const scrollToBottom = useCallback(() => {
		setTimeout(() => {
			couponContentRef.current?.scrollTo({
				top: couponContentWrapperRef.current?.getBoundingClientRect().height || 0 + 300,
				behavior: 'smooth',
			});
		}, 10);
	}, []);

	useEffect(() => {
		if (allBets) {
			const couponContentWrapperHeight = couponContentWrapperRef.current?.getBoundingClientRect().height || 0;
			setTimeout(() => {
				couponContentRef.current?.scrollTo({ top: couponContentWrapperHeight + 250, behavior: 'smooth' });
			}, 100);
		}
	}, [allBets]);

	useEffect(() => {
		scrollToBottom();
	}, [scrollToBottom]);

	const getAkoBonusDetails = (): AkoBonusData => ({
		bonusStepFrom: props.betStatics.newPossibleWinning?.totalBonus?.bonusStepFrom,
		currentBonusValue: props.betStatics.newPossibleWinning?.totalBonus?.currentBonusValue,
		futureBonusValue: props.betStatics.newPossibleWinning?.totalBonus?.futureBonusValue,
		minOutcomeOdds: props.betStatics.newPossibleWinning?.totalBonus?.minOutcomeOdds,
		bonusAmount: props.betStatics.newPossibleWinning?.bonus,
	});

	const { bonusStepFrom, currentBonusValue, futureBonusValue, minOutcomeOdds, bonusAmount } = getAkoBonusDetails();

	const numOfBetsMatchingCriteria = props.allBets.filter((bet) => bet.outcomeOdds >= minOutcomeOdds).length;
	const numOfCouponsToAdd = numOfBetsMatchingCriteria < bonusStepFrom ? bonusStepFrom - numOfBetsMatchingCriteria : 1;

	const isBonusBalance = props.selectedBalanceType === BONUS_BALANCE || !!props.selectedFreeBet;
	const isAkoBonusDeactivated = (!currentBonusValue && !futureBonusValue) || !props.stackValue;

	const isAkoBonusInfoUnavailable = isAkoBonusDeactivated || isBonusBalance || currentBonusValue < FINANCIAL_THRESHOLDS.MIN_AKO_BONUS_PERCENT;

	const betEnable = () => {
		dispatch(createAction(actionTypes.ANALYTICS_ENABLE_BET));
		setDataLayer(BetslipEvents.BET_ENABLE(getAnalyticalData(coupon), enabledBets));
	};

	const betDisable = () => {
		dispatch(createAction(actionTypes.ANALYTICS_DISABLE_BET));
		setDataLayer(BetslipEvents.BET_DISABLE(getAnalyticalData(coupon), disabledBets));
	};

	const handleSelectionOfBet = (value: boolean, id: string) => {
		props.handelSelectedBet(value, id);
		value ? betEnable() : betDisable();
	};

	useEffect(() => {
		const handleResize = () => {
			const parent = couponContentRef.current?.parentElement;
			if (!parent) return;

			const parentHeight = parent.getBoundingClientRect().height;
			const couponListHeight = parentHeight - props.promoConditionsState.promoAccordionWrapperHeight;

			setCouponContentHeight(`${couponListHeight}px`);
		};

		handleResize();

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [props.promoConditionsState.promoAccordionWrapperHeight]);

	return (
		<>
			{isCal && !props.selectedFreeBet && (
				<BetCalculator
					setStakeMethod={setStakeMethod}
					price={props.stackValue}
					onCancelCal={onCancelCal}
					onConfirmCal={onConfirmCal}
					betType={props.betType}
					bets={props.bets}
				/>
			)}
			<div
				ref={couponContentRef}
				style={{ maxHeight: couponContentHeight }}
				className={cn('couponList relative  h-full', {
					'couponList--isAkoBonus': !isAkoBonusInfoUnavailable,
					'overflow--hidden': isCal,
				})}
			>
				<div ref={couponContentWrapperRef} className="flex flex-col gap-2">
					{props.allBets.length > 0 &&
						props.allBets.map((item, index) => (
							<BetslipCouponCard
								key={item.eventId}
								selectedOddsArray={item.betBuilderOddsArray ? item.betBuilderOddsArray : []}
								totalOdds={item.outcomeOdds}
								bet={item}
								isBetSlip
								isBetBuilder={item.isBetBuilder}
								removeBet={props.removeBet}
								handelSelectedBet={handleSelectionOfBet}
								couponType="ako"
								numOfItemsInBetslip={temp}
								warnings={props.warnings}
								completedEvents={props.completedEvents}
								handleIsRestoringBets={props.handleIsRestoringBets}
								betActionErrors={props.betActionErrors}
							/>
						))}
				</div>
				<div className="flex flex-col gap-1">
					{!isAkoBonusDeactivated && (
						<AkoBonusInfoToast
							numOfCouponsToAdd={numOfCouponsToAdd}
							futureBonusValue={futureBonusValue}
							minOutcomeOdds={minOutcomeOdds}
							isBonusBalance={isBonusBalance}
							bonusAmount={bonusAmount}
						/>
					)}
					<ErrorCodes
						errorCodes={props.errorCode}
						betStatics={props.betStatics}
						onCloseError={props.onCloseError}
						scrollToBottom={scrollToBottom}
						betActionErrors={props.betActionErrors}
					/>
				</div>
			</div>
		</>
	);
});

export default AKOBet;
